// src/services/subscriptionGuard.js
import { doc, getDoc, collection, getDocs, query, where, limit } from "firebase/firestore";
import { db } from "../firebase"; // same db you use in LoginScreen


// UTC YYYY-MM-DD (string compare works)
const todayYMD = () => new Date().toISOString().slice(0, 10);
const isOkStatus = (s) => new Set(["ACTIVE", "TRIALING"]).has(String(s || "").toUpperCase());

const OWNER_ALLOW = "0FLhCiGwLiR4BiC5uvpXd6tmLbg1";

async function manualAllowByEmail(email) {
  const em = String(email || "").trim().toLowerCase();
  if (!em) return false;
  const q = query(
    collection(db, "subscribers"),
    where("email", "==", em),
    where("ownerId", "==", OWNER_ALLOW),
    limit(1)
  );
  const snap = await getDocs(q);
  return snap.size > 0;
}

/**
 * Guard: user must have a LIVE subscription that is ACTIVE or TRIALING,
 * and not past endDate (inclusive).
 *
 * Looks up mapping: stripe_customers_by_email/{exactEmail or lowercase} -> { customerId }
 * Then reads: subscribers/{customerId}
 */
 export async function requireActiveSubscription(uid, email) {
   if (!email) return { ok: false, code: "no-email", reason: "User email missing" };

   const exactEmail = String(email);
   const lcEmail = exactEmail.toLowerCase();
  // We'll use this before any "deny" path
  const manualOk = await manualAllowByEmail(exactEmail);

   // 1) email -> customerId (exact-case first, then lowercase)
   let mapSnap = await getDoc(doc(db, "stripe_customers_by_email", exactEmail));
   if (!mapSnap.exists()) {
     mapSnap = await getDoc(doc(db, "stripe_customers_by_email", lcEmail));
   }
   if (!mapSnap.exists()) {

    if (manualOk) return { ok: true, code: "manual-allow", reason: "manual-allow", email: exactEmail };
    return { ok: false, code: "no-map", reason: `No mapping for ${exactEmail}` };
   }

   const customerId = mapSnap.data()?.customerId;
   if (!customerId) {

    if (manualOk) return { ok: true, code: "manual-allow", reason: "manual-allow", email: exactEmail };
    return { ok: false, code: "bad-map", reason: "Mapping missing customerId" };
   }

   // 2) subscriber by customerId
   const subSnap = await getDoc(doc(db, "subscribers", customerId));
   if (!subSnap.exists()) {

    if (manualOk) return { ok: true, code: "manual-allow", reason: "manual-allow", email: exactEmail };
    return { ok: false, code: "no-sub", reason: `No subscriber for ${customerId}` };
   }

   const sub = subSnap.data() || {};
   const status = String(sub.status || "").toUpperCase();
   const liveOk = !!sub.livemode;
   const end = String(sub.endDate || "");       // "YYYY-MM-DD" or ""
   const inTerm = !end || end >= todayYMD();    // inclusive; blank endDate won't block

   const ok = liveOk && isOkStatus(status) && inTerm;

   const reason =
     !liveOk ? "not-live" :
     !isOkStatus(status) ? `status=${status}` :
     !inTerm ? `expired endDate=${end}` :
     "ok";


  // If normal checks failed, allow the manual override
  if (!ok && manualOk) {
    return { ok: true, code: "manual-allow", reason: "manual-allow", sub, customerId, email: exactEmail };
  }
  return { ok, code: ok ? "ok" : "denied", reason, sub, customerId, email: exactEmail };
 }

// ✅ Alias so old imports keep working
export { requireActiveSubscription as checkSubscription };
